import { useEffect, useState, useCallback } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import useApp from './../AppContext';
import {
    callApi, ErrorAlert
} from './utils';

import CrudList from './CrudList';

async function getListLayout(col, userToken, serverUrl) {
    let url = `${serverUrl}/api/layouts/list/${col.layoutId}`;
    if (col.instanceName) {
        url += `?instanceName=${col.instanceName}`;
    }
    return await callApi('GET', url, userToken);
}

async function getChildrenOfList(compositeLayout, masterLayoutId, listLayouts, userToken) {
    let children = [];
    let colsForChildSync = [];
    compositeLayout.rows.forEach(row => {
        row.columns.filter(col => col.masterLayout === masterLayoutId && col.layoutType === 'list').forEach(async col => {
            children.push({
                layoutId: col.layoutId,
                listLayout: listLayouts[col.layoutId]
            });
            col.syncParameters.forEach(sync => {
                colsForChildSync.push(sync.masterField);
            });
        })
    });
    return { children, colsForChildSync };
}

async function analyzeChildrenOfLists(compositeLayout, listLayouts, userToken) {
    const childrenOfLists = {};
    compositeLayout.rows.forEach(row => {
        row.columns.forEach(async col => {
            childrenOfLists[col.layoutId] = await getChildrenOfList(compositeLayout, col.layoutId, listLayouts, userToken);
        });
    });
    return childrenOfLists;
}

async function getListLayouts(compositeLayout, userToken, serverUrl) {
    const layouts = {};
    for (let i = 0; i < compositeLayout.rows.length; i++) {
        for (let j = 0; j < compositeLayout.rows[i].columns.length; j++) {
            layouts[compositeLayout.rows[i].columns[j].layoutId] = await getListLayout(compositeLayout.rows[i].columns[j], userToken, serverUrl);
        }
    }
    return layouts;
}

export default function CompositeLayout(props) {
    const app = useApp();
    const [layout, setLayout] = useState(null);
    const [error, setError] = useState(null);
    const [serverUrl] = useState(app.serverUrl);
    const [selectedRows, setSelectedRows] = useState({});
    const [childrenOfLists, setChildrenOfLists] = useState({});
    const [listLayouts, setListLayouts] = useState({});
    const [isDynamic, setIsDynamic] = useState(false);
    const [settingMode, setSettingMode] = useState(false);
    const [layoutUnderEdit, setLayoutUnderEdit] = useState(null);
    const [layoutIds, setLayoutIds] = useState([]);
    const [relMeta, setRelMeta] = useState({});
    const language = app.sessionToken?.Language;
    useEffect(() => {
        async function getLayout() {
            try {
                let layoutUrl;
                if (props.layoutUrl) {
                    layoutUrl = props.layoutUrl;
                    setIsDynamic(true);
                } else if (props.masterListLayoutId) {
                    let instanceName = '';
                    if (props.masterListInstanceName) instanceName = props.masterListInstanceName;
                    layoutUrl = `api/layouts/composite?objectName=${props.masterListLayoutId}&instanceName=${instanceName}`;
                    setIsDynamic(true);
                }
                if (!layoutUrl || !serverUrl) return;
                let layout = await callApi('GET', `${serverUrl}/${layoutUrl}`, props.userToken);
                const listLayouts = await getListLayouts(layout, props.userToken, serverUrl);
                let layoutIds = [];
                for (let i = 0; i < Object.keys(listLayouts).length; i++) {
                    layoutIds.push({ queryObject: listLayouts[Object.keys(listLayouts)[i]].queryObject, caption: listLayouts[Object.keys(listLayouts)[i]].caption })
                }
                setLayoutIds(layoutIds);
                setChildrenOfLists(await analyzeChildrenOfLists(layout, listLayouts, props.userToken));
                setListLayouts(listLayouts);
                setLayout(layout);
                setSettingMode(false);
            } catch (err) {
                setError(err.message);
            }
        };
        getLayout();
    }, [props.layoutUrl, props.masterListLayoutId, props.masterListInstanceName, props.userToken, serverUrl]);

    const listRowSelected = useCallback((layoutId, row) => {
        setSelectedRows((prev) => {
            prev = { ...prev };
            prev[layoutId] = row;
            return prev;
        });
    }, []);

    const layoutSettingsHandler = useCallback(() => {
        let layoutUnderEdit = JSON.parse(JSON.stringify(layout));
        setLayoutUnderEdit(layoutUnderEdit);
        setSettingMode(true);
    }, [layout]);

    function createCols(r, rowIndex) {
        return r.columns.map((c, i) => {
            let url = `api/layouts/list/${c.layoutId}`;
            if (c.instanceName) {
                url += `?instanceName=${c.instanceName}`;
            }
            let parentSelectedRow = selectedRows[c.masterLayout];
            let onLayoutSettingsClicked = null;
            if (isDynamic && rowIndex === 0 && i === 0) onLayoutSettingsClicked = layoutSettingsHandler;
            return <Col key={i} xs={c.width} className="" style={{ height: '100%' }}>
                {c.layoutType === "list" ? <CrudList
                    dataEngine={props.dataEngine}
                    dataContext={props.dataContext}
                    childrenOfList={childrenOfLists[c.layoutId]}
                    userToken=""
                    layoutUrl={url}
                    layout={listLayouts[c.layoutId]}
                    rowSelected={listRowSelected}
                    syncParameters={c.syncParameters}
                    parentSelectedRow={parentSelectedRow}
                    onLayoutSettingsClicked={onLayoutSettingsClicked} /> : null}
            </Col>
        });
    }

    function createRows() {
        if (!layout || !layout.rows) return null;
        return layout.rows.map((r, i) => {
            return <Row key={i} className="p-0 w-100 mx-auto" style={{ height: r.height + "%" }}>
                {createCols(r, i)}
            </Row>
        });
    }

    function getRelMeta(objectName) {
        if (!objectName || !relMeta[objectName]) return [];
        return relMeta[objectName];
    }

    async function handleSettingsChange(e, rowIndex, colIndex, columnName) {
        setLayoutUnderEdit(prev => {
            prev = JSON.parse(JSON.stringify(prev));
            prev.rows[rowIndex].columns[colIndex][columnName] = e.target.value;
            return prev;
        });
        if (columnName === "layoutId") {
            if (!layoutIds.find(l => l.queryObject === e.target.value)) {
                setLayoutIds(prev => {
                    prev = [...prev];
                    prev.push({ queryObject: e.target.value, caption: e.target.options[e.target.selectedIndex].innerText });
                    return prev;
                })
            };
        }
        if (columnName === 'masterLayout' && e.target.value) {
            let currentRelMeta = await callApi('GET', `${serverUrl}/api/layouts/children/${e.target.value}`, props.userToken);
            // const currentRelMeta1 = [{
            //     childObject: "Lu3Ways",
            //     caption: "Lu3 Ways",
            //     syncParameters: [
            //         {
            //             parameterField: "ItemId",
            //             masterField: "ItemId"
            //         }
            //     ]
            // }, {
            //     childObject: "Item_ItemAttributes",
            //     caption: "Item Attributes",
            //     syncParameters: [
            //         {
            //             parameterField: "ItemId",
            //             masterField: "ItemId"
            //         }
            //     ]
            // }];
            setRelMeta(prev => {
                prev = JSON.parse(JSON.stringify(prev));
                prev[e.target.value] = currentRelMeta;
                return prev;
            })
        }
    }

    function deleteCol(rowIndex, colIndex) {
        setLayoutUnderEdit(prev => {
            prev = JSON.parse(JSON.stringify(prev));
            prev.rows[rowIndex].columns.splice(colIndex, 1);
            if (!prev.rows[rowIndex].columns.length) {
                prev.rows.splice(rowIndex, 1);
            }
            return prev;
        });
    }

    function settingsCard(col, rowIndex, colIndex) {
        return (
            <Card>
                <Card.Header>{col.layoutId}</Card.Header>
                <Card.Body>
                    {rowIndex !== 0 || colIndex !== 0 ? <InputGroup className="mb-3">
                        <InputGroup.Text id="basic-addon1">Parent</InputGroup.Text>
                        <Form.Control
                            as="select"
                            value={col.masterLayout}
                            disabled={rowIndex === 0 && colIndex === 0}
                            onChange={e => handleSettingsChange(e, rowIndex, colIndex, 'masterLayout')}
                        >
                            <option value={''}></option>
                            {layoutIds.map((enm, i) =>
                                <option key={i} value={enm.queryObject}>{enm.caption}</option>
                            )}
                        </Form.Control>
                    </InputGroup> : null}
                    {rowIndex === 0 && colIndex === 0 ? <InputGroup className="mb-3">
                        <InputGroup.Text id="basic-addon1">Object</InputGroup.Text>
                        <Form.Control
                            placeholder="Object"
                            aria-label="Object"
                            aria-describedby="basic-addon1"
                            value={col.layoutId}
                            disabled={rowIndex === 0 && colIndex === 0}
                            onChange={e => handleSettingsChange(e, rowIndex, colIndex, 'layoutId')}
                        />
                    </InputGroup> : null}
                    {rowIndex !== 0 || colIndex !== 0 ? <InputGroup className="mb-3">
                        <InputGroup.Text id="basic-addon1">Object</InputGroup.Text>
                        <Form.Control
                            as="select"
                            value={col.layoutId}
                            disabled={rowIndex === 0 && colIndex === 0}
                            onChange={e => handleSettingsChange(e, rowIndex, colIndex, 'layoutId')}
                        >
                            <option value={''}></option>
                            {getRelMeta(col.masterLayout).map((enm, i) =>
                                <option key={i} value={enm.childObject}>{enm.caption}</option>
                            )}
                        </Form.Control>
                    </InputGroup> : null}
                    <InputGroup className="mb-3">
                        <InputGroup.Text id="basic-addon1">Width</InputGroup.Text>
                        <Form.Control
                            placeholder="Width"
                            aria-label="Width"
                            aria-describedby="basic-addon1"
                            value={col.width}
                            onChange={e => handleSettingsChange(e, rowIndex, colIndex, 'width')}
                        />
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <InputGroup.Text id="basic-addon1">Instance</InputGroup.Text>
                        <Form.Control
                            placeholder="Instance Name"
                            aria-label="Instance Name"
                            aria-describedby="basic-addon1"
                            value={col.instanceName}
                            disabled={rowIndex === 0 && colIndex === 0}
                            onChange={e => handleSettingsChange(e, rowIndex, colIndex, 'instanceName')}
                        />
                    </InputGroup>
                </Card.Body>
                {rowIndex !== 0 || colIndex !== 0 ? <Card.Footer>
                    <Button variant="" className='me-2 border' onClick={() => { deleteCol(rowIndex, colIndex) }}>Delete</Button>
                </Card.Footer> : null}
            </Card>
        );
    }

    function appendCol(rowIndex) {
        let masterLayout = '';
        /*if (layoutUnderEdit && layoutUnderEdit.rows[0] && layoutUnderEdit.rows[0].columns[0]){
            masterLayout=layoutUnderEdit.rows[0].columns[0].layoutId;
        }*/
        let prevColsCnt = 0;
        if (rowIndex !== -1) {
            prevColsCnt = layoutUnderEdit.rows[layoutUnderEdit.rows.length - 1].columns.length;
        }
        setLayoutUnderEdit(prev => {
            prev = JSON.parse(JSON.stringify(prev));
            let finalRowIndex = rowIndex;
            const newCol = {
                layoutType: 'list',
                layoutId: '...',
                masterLayout,
                width: 12
            };
            if (finalRowIndex === -1) {
                finalRowIndex = prev.rows.length;
                prev.rows.push({ height: prev.rows[finalRowIndex - 1].height / 2, columns: [] })
                prev.rows[finalRowIndex - 1].height = prev.rows[finalRowIndex - 1].height / 2
            } else {
                //prevent strange behaviour that adds tow cols instead of 1
                if (prev.rows[finalRowIndex].columns.length === prevColsCnt + 1) return prev;
                if (prev.rows[finalRowIndex].columns && prev.rows[finalRowIndex].columns.length !== 0) {
                    newCol.width = prev.rows[finalRowIndex].columns[prev.rows[finalRowIndex].columns.length - 1].width = prev.rows[finalRowIndex].columns[prev.rows[finalRowIndex].columns.length - 1].width / 2;
                }
            }
            //prevent strange behaviour that adds tow cols instead of 1
            if (prev.rows[finalRowIndex].columns.length === prevColsCnt + 1) return prev;
            prev.rows[finalRowIndex].columns.push(newCol);
            return prev;
        });
    }

    function createSettingsCols(r, rowIndex) {
        const cols = r.columns.map((c, i) => {
            return <Col key={i} xs={3} className="border" style={{ height: '100%' }}>
                {c.layoutType === "list" ? settingsCard(c, rowIndex, i) : null}
            </Col>
        });
        return <Row>
            {cols}
            <Col><Button variant="" className='me-2 border' onClick={() => appendCol(rowIndex)}>Add List</Button></Col>
        </Row>
    }

    async function saveLayout() {
        setError('');
        if (!layoutUnderEdit.name) {
            setError(`Please set a layout name.`);
            return;
        }
        for (let rowIndex = 0; rowIndex < layoutUnderEdit.rows.length; rowIndex++) {
            const row = layoutUnderEdit.rows[rowIndex];
            for (let colIndex = 0; colIndex < row.columns.length; colIndex++) {
                if (rowIndex === 0 && colIndex === 0) continue;
                const col = row.columns[colIndex];
                if (!col.masterLayout) {
                    setError(`Please set parent object on row ${rowIndex + 1} column ${colIndex + 1}`);
                    break;
                }
                if (!col.layoutId) {
                    setError(`Please set parent object on row ${rowIndex + 1} column ${colIndex + 1}`);
                    break;
                }
                const curRelMeta = relMeta[col.masterLayout].find(r => r.childObject === col.layoutId);
                if (!curRelMeta) {
                    setError(`No rel info found for row ${rowIndex + 1} column ${colIndex + 1} and object ${col.layoutId}`);
                    break;
                }
                col.syncParameters = relMeta[col.masterLayout].find(r => r.childObject === col.layoutId).syncParameters;
            }
        }
        try {
            await callApi('POST', `${serverUrl}/api/customlayouts`, props.userToken, layoutUnderEdit);
        } catch (err) {
            setError(err.message);
            return;
        }
        setLayout(JSON.parse(JSON.stringify(layoutUnderEdit)));
        setSettingMode(false);
    }

    function createSettingsView() {
        if (!layoutUnderEdit || !layoutUnderEdit.rows) return null;
        const rows = layoutUnderEdit.rows.map((r, i) => {
            return <Row key={i} className="mt-4 p-0 border">
                {createSettingsCols(r, i)}
            </Row>
        });
        return <>
            <Navbar className="justify-content-left bt-1 py-1">
                <Container>
                    <Nav className="ms-0">
                        <Form.Control size="sm" className="ms-0" placeholder='Name' value={layoutUnderEdit.name} onChange={(e) => setLayoutUnderEdit(prev => { prev = { ...prev }; prev.name = e.target.value; return prev; })} />
                        <Button variant="" className='me-2 border' onClick={() => saveLayout()}>Save</Button>
                        <Button variant="" className='me-2 border' onClick={() => { setSettingMode(false) }}>Undo</Button>
                    </Nav>
                </Container>
            </Navbar>
            {rows}
            <Row className="mt-4"><Col sx={1}><Button variant="" className='me-2 border' onClick={() => appendCol(-1)}>Add List</Button></Col></Row>
        </>
    }

    function createView() {
        if (settingMode) return createSettingsView();
        else return createRows();
    }

    return <>
        <ErrorAlert message={error} setMessage={setError} language={language}/>
        {createView()}
    </>
}
